import React, { useMemo, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { MapContainer, TileLayer, Polyline, useMap, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

/**
 * Component to Fit Bounds of the map to the polyline
 */
const MapConfigurator = ({ points, minimal }: { points: [number, number][], minimal: boolean }) => {
    const map = useMap();

    useEffect(() => {
        if (points && points.length > 0) {
            const bounds = L.latLngBounds(points);
            // Add slight padding to the bounds so the route isn't touching edges
            const paddedBounds = bounds.pad(0.1);

            map.fitBounds(paddedBounds, {
                padding: [20, 20],
                maxZoom: 16,
                animate: false
            });

            if (!minimal) {
                // Constrain the map to the initial bounds
                // maxBoundsViscosity 1.0 makes it fully solid (no bouncing past edges)
                map.setMaxBounds(paddedBounds);
                map.options.maxBoundsViscosity = 1.0;

                // Prevent zooming out beyond the initial view
                // We calculate the zoom level that fits these bounds
                const fitZoom = map.getBoundsZoom(paddedBounds);
                map.setMinZoom(fitZoom);
            }
        }
    }, [points, map, minimal]);

    return null;
};

/**
 * Decode Google Polyline format
 */
function decodePolyline(encoded: string): [number, number][] {
    const points: [number, number][] = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < encoded.length) {
        let shift = 0;
        let result = 0;
        let byte: number;

        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
        lat += deltaLat;

        shift = 0;
        result = 0;

        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
        lng += deltaLng;

        points.push([lat / 1e5, lng / 1e5]);
    }

    return points;
}

// --- Icons & Helpers for Markers/Arrows ---

// Start Marker Icon (Green Dot)
const startIcon = L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="
        background-color: #4CAF50;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 0 4px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
});

// End Marker Icon (Red Square/Dot)
const endIcon = L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="
        background-color: #F44336;
        width: 12px;
        height: 12px;
        border-radius: 2px;
        border: 2px solid white;
        box-shadow: 0 0 4px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
});

// Helper to create a rotated arrow icon
const createArrowIcon = (angle: number, color: string) => L.divIcon({
    className: 'arrow-icon',
    html: `<div style="
        transform: rotate(${angle}deg);
        display: flex;
        justify-content: center;
        align-items: center;
    ">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L12 20M12 4L6 10M12 4L18 10" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    </div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7], // Center the rotation
});

// Helper to calculate distance between two points
const getSegmentDistance = (p1: [number, number], p2: [number, number]) => {
    return L.latLng(p1[0], p1[1]).distanceTo(L.latLng(p2[0], p2[1]));
}

// Calculate Bearing
const getBearing = (start: [number, number], end: [number, number]) => {
    const startLat = (start[0] * Math.PI) / 180;
    const startLng = (start[1] * Math.PI) / 180;
    const endLat = (end[0] * Math.PI) / 180;
    const endLng = (end[1] * Math.PI) / 180;

    const y = Math.sin(endLng - startLng) * Math.cos(endLat);
    const x =
        Math.cos(startLat) * Math.sin(endLat) -
        Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);
    const brng = (Math.atan2(y, x) * 180) / Math.PI;
    return (brng + 360) % 360;
};

// Main function to get arrow positions
const getArrowPoints = (points: [number, number][], numArrows: number) => {
    if (points.length < 2) return [];

    // 1. Calculate total distance
    let totalDist = 0;
    const dists: number[] = [0]; // Cumulative distance at each point
    for (let i = 0; i < points.length - 1; i++) {
        const d = getSegmentDistance(points[i], points[i + 1]);
        totalDist += d;
        dists.push(totalDist);
    }

    if (totalDist === 0) return [];

    // 2. Determine target distances
    // If numArrows is 3, we want at 25%, 50%, 75%? Or fixed interval?
    // User said "equally spaced across the polyline".
    // Let's do (i + 1) * (totalDist / (numArrows + 1))
    const arrowData: { position: [number, number], angle: number }[] = [];
    const step = totalDist / (numArrows + 1);

    let currentArrowIdx = 1;
    let currentTarget = step * currentArrowIdx;

    // 3. Find points
    for (let i = 0; i < points.length - 1; i++) {
        // If the current segment contains the target distance
        // segment start dist = dists[i]
        // segment end dist = dists[i+1]

        while (currentArrowIdx <= numArrows && currentTarget >= dists[i] && currentTarget < dists[i + 1]) {
            // Found the segment
            const p1 = points[i];
            const p2 = points[i + 1];

            // Interpolate
            const segmentDist = dists[i + 1] - dists[i];
            const distIntoSegment = currentTarget - dists[i];

            // Avoid division by zero
            const fraction = segmentDist > 0 ? distIntoSegment / segmentDist : 0;

            const lat = p1[0] + (p2[0] - p1[0]) * fraction;
            const lng = p1[1] + (p2[1] - p1[1]) * fraction;

            const angle = getBearing(p1, p2);

            arrowData.push({
                position: [lat, lng],
                angle
            });

            currentArrowIdx++;
            currentTarget = step * currentArrowIdx;
        }
    }

    return arrowData;
};

interface PolylineMapProps {
    /** Encoded polyline string from Strava */
    polyline?: string;
    /** Height of the map container */
    height?: number | string;
    /** Width of the map container */
    width?: number | string;
    /** Stroke width for the route line */
    strokeWidth?: number;
    /** Whether to show a minimalist style (for thumbnails) */
    minimal?: boolean;
    /** Whether to enable hero mode with fullscreen functionality */
    isHeroMode?: boolean;
    /** Callback when fullscreen button is clicked */
    onToggleFullscreen?: () => void;
    /** Whether the map is currently in fullscreen mode (hero mode only) */
    isFullscreen?: boolean;
}

/**
 * PolylineMap Component
 * 
 * Renders a Strava activity route using React Leaflet.
 * Uses CartoDB tiles for a beautiful Strava-like aesthetic (Light/Dark).
 */
export const PolylineMap: React.FC<PolylineMapProps> = ({
    polyline,
    height = 400,
    width = '100%',
    strokeWidth = 3,
    minimal = false,
    isHeroMode = false,
    onToggleFullscreen,
    isFullscreen = false,
}) => {
    const theme = useTheme();

    // Decide Map Style based on Theme
    // Strava uses a custom mapbox style, mostly light grey/white or dark grey/black
    // CartoDB Positron is very close to Strava Light
    // CartoDB Dark Matter is very close to Strava Dark
    const isDark = theme.palette.mode === 'dark';

    const tileUrl = isDark
        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

    const routeColor = '#fc4c02'; // Strava Orange

    const points = useMemo(() => {
        if (!polyline) return [];
        try {
            return decodePolyline(polyline);
        } catch (e) {
            console.error('Failed to decode polyline', e);
            return [];
        }
    }, [polyline]);

    // Calculate arrows
    const arrows = useMemo(() => {
        if (!points || points.length < 2 || minimal) return [];
        // Determine number of arrows based on complexity or length?
        // Fixed number feels safer for UI clutter.
        return getArrowPoints(points, 5); // 5 arrows
    }, [points, minimal]);

    if (!polyline || points.length === 0) {
        return (
            <Box
                sx={{
                    width,
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.action.hover,
                    borderRadius: minimal ? 1 : 2,
                    color: 'text.secondary',
                }}
            >
                No route data
            </Box>
        );
    }

    // Calculate center nicely for initial load (avoids grey flash)
    const centerLat = points.reduce((sum, p) => sum + p[0], 0) / points.length;
    const centerLng = points.reduce((sum, p) => sum + p[1], 0) / points.length;

    return (
        <Box
            sx={{
                width,
                height,
                borderRadius: minimal ? 1 : 2,
                overflow: 'hidden',
                border: minimal ? 'none' : `1px solid ${theme.palette.divider}`,
                position: 'relative',
                transition: 'height 300ms ease-in-out',
                // Force map z-index to avoid issues with popovers
                '& .leaflet-container': {
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    background: isDark ? '#242424' : '#f5f5f5', // Match tile background
                },
                // Custom Dark Mode Styling for Leaflet Controls
                '& .leaflet-control-zoom': {
                    border: isDark ? 'none' : '2px solid rgba(0,0,0,0.2)',
                    boxShadow: isDark ? '0 2px 6px rgba(0,0,0,0.5)' : undefined,
                },
                '& .leaflet-bar a': {
                    backgroundColor: isDark ? '#333 !important' : '#fff',
                    color: isDark ? '#fff !important' : '#000',
                    borderBottom: isDark ? '1px solid #444 !important' : '1px solid #ccc',
                },
                '& .leaflet-bar a:hover': {
                    backgroundColor: isDark ? '#444 !important' : '#f4f4f4',
                },
                '& .leaflet-bar a:first-of-type': {
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                },
                '& .leaflet-bar a:last-child': {
                    borderBottom: 'none !important',
                    borderBottomLeftRadius: '4px',
                    borderBottomRightRadius: '4px',
                },
            }}
        >
            {/* Fullscreen Button for Hero Mode */}
            {isHeroMode && onToggleFullscreen && (
                <Box
                    onClick={onToggleFullscreen}
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 2,
                        p: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        transition: 'all 200ms',
                        '&:hover': {
                            bgcolor: 'action.hover',
                            boxShadow: 3,
                        },
                    }}
                    title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        {!isFullscreen ? (
                            // Fullscreen icon (expand)
                            <>
                                <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M16 3H19C20.1046 3 21 3.89543 21 5V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 21H5C3.89543 21 3 20.1046 3 19V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M16 21H19C20.1046 21 21 20.1046 21 19V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </>
                        ) : (
                            // Exit fullscreen icon (collapse)
                            <>
                                <path d="M8 8H5C3.89543 8 3 7.10457 3 6V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M16 8H19C20.1046 8 21 7.10457 21 6V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 16H5C3.89543 16 3 16.8954 3 18V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M16 16H19C20.1046 16 21 16.8954 21 18V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </>
                        )}
                    </svg>
                </Box>
            )}

            <MapContainer
                center={[centerLat, centerLng]}
                zoom={13}
                scrollWheelZoom={!minimal}
                dragging={!minimal}
                zoomControl={!minimal}
                attributionControl={!minimal}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    url={tileUrl}
                />

                <Polyline
                    positions={points}
                    pathOptions={{
                        color: routeColor,
                        weight: strokeWidth,
                        opacity: 1,
                        lineCap: 'round',
                        lineJoin: 'round'
                    }}
                />

                {/* Start Marker */}
                {!minimal && points.length > 0 && (
                    <Marker position={points[0]} icon={startIcon} title="Start" zIndexOffset={100} />
                )}

                {/* End Marker */}
                {!minimal && points.length > 0 && (
                    <Marker position={points[points.length - 1]} icon={endIcon} title="End" zIndexOffset={100} />
                )}

                {/* Direction Arrows */}
                {!minimal && arrows.map((arrow, idx) => (
                    <Marker
                        key={idx}
                        position={arrow.position}
                        icon={createArrowIcon(arrow.angle, isDark ? '#fff' : '#000')}
                        zIndexOffset={50}
                        interactive={false}
                    />
                ))}

                <MapConfigurator points={points} minimal={minimal} />
            </MapContainer>
        </Box>
    );
};

export default PolylineMap;
